import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import type {
  CreateIrrigationProblemTypeInput,
  UpdateIrrigationProblemTypeInput,
} from '@lib/input/irrigation-problem-type.input'
import { irrigationProblemTypeService } from '@/services/IrrigationProblemTypeService'
import { irrigationProblemTypeKeys } from '@/hooks/queryKeys'

/**
 * The irrigation problem directory, live and archived. Long-lived: it
 * changes when somebody edits Settings, not while a survey runs.
 */
export function useIrrigationProblemTypes(): UseQueryResult<
  IrrigationProblemTypeDto[]
> {
  return useQuery({
    queryKey: irrigationProblemTypeKeys.list(),
    queryFn: async () =>
      (await irrigationProblemTypeService.list()).problemTypes,
    staleTime: 5 * 60_000,
  })
}

/** Every directory change refreshes the directory, and only the directory. */
function useDirectoryMutation<TInput>(
  run: (input: TInput) => Promise<IrrigationProblemTypeDto>,
): UseMutationResult<IrrigationProblemTypeDto, Error, TInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: irrigationProblemTypeKeys.all,
      })
    },
  })
}

export function useCreateIrrigationProblemType(): UseMutationResult<
  IrrigationProblemTypeDto,
  Error,
  CreateIrrigationProblemTypeInput
> {
  return useDirectoryMutation((input: CreateIrrigationProblemTypeInput) =>
    irrigationProblemTypeService.create(input),
  )
}

export function useUpdateIrrigationProblemType(): UseMutationResult<
  IrrigationProblemTypeDto,
  Error,
  { id: string; input: UpdateIrrigationProblemTypeInput }
> {
  return useDirectoryMutation(
    ({ id, input }: { id: string; input: UpdateIrrigationProblemTypeInput }) =>
      irrigationProblemTypeService.update(id, input),
  )
}

export function useArchiveIrrigationProblemType(): UseMutationResult<
  IrrigationProblemTypeDto,
  Error,
  { id: string; archived: boolean }
> {
  return useDirectoryMutation(
    ({ id, archived }: { id: string; archived: boolean }) =>
      archived
        ? irrigationProblemTypeService.archive(id)
        : irrigationProblemTypeService.restore(id),
  )
}
